import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle,
  Badge, Spinner, Table, Progress
} from "reactstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import WelcomeComp from "./WelcomeComp";
import MonthlyEarning from "./MonthlyEarning";
import ActivityComp from "./ActivityComp";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const Dashboard = () => {
  document.title = "Anasayfa | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const isAdmin = authUser?.HESAP_TURU === "Admin";
  const userId = authUser?.ID;

  const fetchOpts = useMemo(() => ({
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }), [token]);

  // ─── STATS STATE ─────────────────────────────────────────────
  const [stats, setStats] = useState({
    totalUsers: 0, newUsersThisMonth: 0,
    activeListings: 0, totalListings: 0,
    totalOrders: 0, pendingOrders: 0,
    totalRevenue: 0, monthlyRevenue: 0,
    pendingOffers: 0, acceptedOffers: 0,
    activeMezats: 0, totalStores: 0,
    // non-admin
    myListings: 0, myOrders: 0, myOffers: 0, myRevenue: 0,
    isLoading: true,
  });

  // ─── CHART DATA ───────────────────────────────────────────────
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  // ─── TABLES DATA ──────────────────────────────────────────────
  const [recentOrders, setRecentOrders] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [topCities, setTopCities] = useState([]);
  const [orderStatusDist, setOrderStatusDist] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [myRecentOffers, setMyRecentOffers] = useState([]);

  // ─── FETCH ───────────────────────────────────────────────────
  const fetchDashboard = useCallback(async () => {
    if (!userId) return;
    const year = new Date().getFullYear();
const monthStart = new Date(year, new Date().getMonth(), 1).toISOString();
  const yearStart = `${year}-01-01T00:00:00Z`; // 👈 Buraya 'Z' eklendi
    try {
      if (isAdmin) {
        // ── ADMIN: tüm veriler ──────────────────────────────
        const [
          usersRes, storesRes, listingsRes, ordersRes,
          offersRes, mezatRes, odemeRes,
          recentOrdersRes, newUsersRes, ilanKatRes,
          ilanIlRes, odemeMonthlyRes,
        ] = await Promise.allSettled([
          fetch(`${BASE_URL}/KULLANICILAR?$select=ID,HESAP_TURU,OLUSTURMA_TARIHI`, fetchOpts),
          fetch(`${BASE_URL}/MAGAZALAR?$select=ID`, fetchOpts),
          fetch(`${BASE_URL}/ILANLAR?$select=ID,AKTIF_DURUM,KATEGORI_ID,KONUM_IL,OLUSTURMA_TARIHI`, fetchOpts),
          fetch(`${BASE_URL}/SIPARIS?$select=ID,SIPARIS_DURUMU,TOPLAM_TUTAR,SIPARIS_TARIHI`, fetchOpts),
          fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$select=ID,DURUM`, fetchOpts),
          fetch(`${BASE_URL}/MEZATLAR?$select=ID,BITIS_TARIHI,TEKLIF_ACIK`, fetchOpts),
          fetch(`${BASE_URL}/ODEME?$select=ID,ODEME_MIKTARI,ODEME_TARIHI,ODEME_DURUMU`, fetchOpts),
          fetch(`${BASE_URL}/SIPARIS?$top=8&$orderby=SIPARIS_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD)`, fetchOpts),
          fetch(`${BASE_URL}/KULLANICILAR?$top=6&$orderby=ID desc&$select=ID,AD_SOYAD,HESAP_TURU,OLUSTURMA_TARIHI,Mail_Adress`, fetchOpts),
          fetch(`${BASE_URL}/ILANLAR?$select=KATEGORI_ID&$top=5000`, fetchOpts),
          fetch(`${BASE_URL}/ILANLAR?$select=KONUM_IL&$top=2000`, fetchOpts),
          fetch(`${BASE_URL}/ODEME?$select=ODEME_MIKTARI,ODEME_TARIHI&$filter=ODEME_TARIHI ge ${yearStart}`, fetchOpts),
        ]);

        const safe = async (res) => {
          if (res.status !== "fulfilled") return null;
          try { return await res.value.json(); } catch { return null; }
        };

        const [
          usersData, storesData, listingsData, ordersData,
          offersData, mezatData, odemeData,
          recentOrdersData, newUsersData, ilanKatData,
          ilanIlData, odemeMonthlyData,
        ] = await Promise.all([
          safe(usersRes), safe(storesRes), safe(listingsRes), safe(ordersRes),
          safe(offersRes), safe(mezatData2 => mezatData2, mezatRes), safe(odemeRes),
          safe(recentOrdersRes), safe(newUsersRes), safe(ilanKatRes),
          safe(ilanIlRes), safe(odemeMonthlyRes),
        ]);

        const users = usersData?.value || [];
        const listings = listingsData?.value || [];
        const orders = ordersData?.value || [];
        const offers = offersData?.value || [];
        const odeme = odemeData?.value || [];
        const mezats = mezatData?.value || [];

        // Stats hesapla
        const now = new Date();
        const newUsersMonth = users.filter(u => {
          if (!u.OLUSTURMA_TARIHI) return false;
          const d = new Date(u.OLUSTURMA_TARIHI);
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        }).length;

        const totalRevenue = odeme.reduce((s, o) => s + (Number(o.ODEME_MIKTARI) || 0), 0);
        const monthlyRev = odeme.filter(o => o.ODEME_TARIHI && new Date(o.ODEME_TARIHI) >= new Date(monthStart))
          .reduce((s, o) => s + (Number(o.ODEME_MIKTARI) || 0), 0);

        setStats({
          totalUsers: users.length,
          newUsersThisMonth: newUsersMonth,
          activeListings: listings.filter(l => l.AKTIF_DURUM === "aktif").length,
          totalListings: listings.length,
          totalOrders: orders.length,
          pendingOrders: orders.filter(o => o.SIPARIS_DURUMU === "Bekliyor").length,
          totalRevenue,
          monthlyRevenue: monthlyRev,
          pendingOffers: offers.filter(o => o.DURUM === 0 || o.DURUM === null).length,
          acceptedOffers: offers.filter(o => o.DURUM === 1).length,
          activeMezats: mezats.filter(m => m.TEKLIF_ACIK).length,
          totalStores: storesData?.value?.length || 0,
          isLoading: false,
        });

        // Son siparişler
        setRecentOrders(recentOrdersData?.value || []);

        // Yeni üyeler
        setNewUsers(newUsersData?.value || []);

        // Top kategoriler (ilan sayısına göre)
        const katCount = {};
        (ilanKatData?.value || []).forEach(i => {
          if (i.KATEGORI_ID) katCount[i.KATEGORI_ID] = (katCount[i.KATEGORI_ID] || 0) + 1;
        });
        const totalCatIlan = Object.values(katCount).reduce((a, b) => a + b, 0);
        // Fetch category names for top 5
        const topKatIds = Object.entries(katCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([id, cnt]) => ({ id: parseInt(id), cnt }));

        if (topKatIds.length) {
          try {
            const idList = topKatIds.map(k => k.id).join(",");
            const katRes2 = await fetch(
              `${BASE_URL}/KATEGORILER?$filter=ID in (${idList})&$select=ID,AD`,
              fetchOpts
            );
            const katNames = await katRes2.json();
            const nameMap = {};
            (katNames.value || []).forEach(k => { nameMap[k.ID] = k.AD; });
            setTopCategories(
              topKatIds.map((k, i) => ({
                name: nameMap[k.id] || `Kategori #${k.id}`,
                count: k.cnt,
                percent: totalCatIlan ? Math.round((k.cnt / totalCatIlan) * 100) : 0,
                color: ["primary", "success", "warning", "info", "danger"][i],
              }))
            );
          } catch { setTopCategories([]); }
        }

        // Top şehirler
        const ilCount = {};
        let totalIl = 0;
        (ilanIlData?.value || []).forEach(i => {
          if (i.KONUM_IL) {
            const il = i.KONUM_IL.trim().toLocaleUpperCase("tr-TR");
            ilCount[il] = (ilCount[il] || 0) + 1;
            totalIl++;
          }
        });
        const topIlArr = Object.entries(ilCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name, count], i) => ({
            name, count,
            percent: totalIl ? Math.round((count / totalIl) * 100) : 0,
            color: ["primary", "success", "warning", "info", "danger"][i],
          }));
        setTopCities(topIlArr);

        // Sipariş durum dağılımı
        const statusMap = {};
        orders.forEach(o => {
          const s = o.SIPARIS_DURUMU || "Bilinmiyor";
          statusMap[s] = (statusMap[s] || 0) + 1;
        });
        setOrderStatusDist(
          Object.entries(statusMap)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([name, count]) => ({ name, count, percent: orders.length ? Math.round((count / orders.length) * 100) : 0 }))
        );

        // Aylık gelir grafiği
        const monthlyRevArr = new Array(12).fill(0);
        const monthlyOrderArr = new Array(12).fill(0);
        const monthlyIlanArr = new Array(12).fill(0);

        (odemeMonthlyData?.value || []).forEach(o => {
          if (o.ODEME_TARIHI) {
            const m = new Date(o.ODEME_TARIHI).getMonth();
            monthlyRevArr[m] += Number(o.ODEME_MIKTARI) || 0;
          }
        });
        orders.forEach(o => {
          if (o.SIPARIS_TARIHI) {
            const d = new Date(o.SIPARIS_TARIHI);
            if (d.getFullYear() === year) monthlyOrderArr[d.getMonth()]++;
          }
        });
        listings.forEach(l => {
          if (l.OLUSTURMA_TARIHI) {
            const d = new Date(l.OLUSTURMA_TARIHI);
            if (d.getFullYear() === year) monthlyIlanArr[d.getMonth()]++;
          }
        });

        setChartSeries([
          { name: "Aylık Gelir (₺)", type: "area", data: monthlyRevArr.map(v => Math.round(v)) },
          { name: "Sipariş Sayısı", type: "bar", data: monthlyOrderArr },
          { name: "Yeni İlan", type: "bar", data: monthlyIlanArr },
        ]);

        // Son ödemeler
        try {
          const payRes = await fetch(
            `${BASE_URL}/ODEME?$top=8&$orderby=ODEME_TARIHI desc&$expand=SIPARIS($expand=KULLANICILAR($select=AD_SOYAD))`,
            fetchOpts
          );
          const payData = await payRes.json();
          setRecentPayments(payData.value || []);
        } catch { }

      } else {
        // ── NON-ADMIN ───────────────────────────────────────
        const userFilter = authUser.HESAP_TURU === "Satıcı"
          ? `KIMDEN_ID eq ${userId}`
          : `KIMDEN_ID eq ${userId}`;

        const [listRes, ordRes, offerRes, odemeRes] = await Promise.allSettled([
          fetch(`${BASE_URL}/ILANLAR?$filter=${userFilter}&$select=ID,AKTIF_DURUM,OLUSTURMA_TARIHI`, fetchOpts),
          fetch(`${BASE_URL}/SIPARIS?$filter=SATICI_ID eq ${userId}&$select=ID,SIPARIS_DURUMU,TOPLAM_TUTAR,SIPARIS_TARIHI&$top=500`, fetchOpts),
          fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$filter=KULLANICI_ID eq ${userId}&$select=ID,DURUM,TEKLIF_TUTARI,TEKLIF_TARIHI&$top=8&$orderby=TEKLIF_TARIHI desc`, fetchOpts),
          fetch(`${BASE_URL}/ODEME?$top=8&$orderby=ODEME_TARIHI desc&$expand=SIPARIS`, fetchOpts),
        ]);

        const safe2 = async (res) => {
          if (res.status !== "fulfilled") return null;
          try { return await res.value.json(); } catch { return null; }
        };
        const [listData, ordData, offerData, odemeData2] = await Promise.all([
          safe2(listRes), safe2(ordRes), safe2(offerRes), safe2(odemeRes),
        ]);

        const myListings = listData?.value || [];
        const myOrders = ordData?.value || [];
        const myOffers = offerData?.value || [];
        const myOdeme = odemeData2?.value || [];

        const myRevenue = myOrders.reduce((s, o) => s + (Number(o.TOPLAM_TUTAR) || 0), 0);

        // Aylık ilan grafiği
        const monthlyArr = new Array(12).fill(0);
        myListings.forEach(l => {
          if (l.OLUSTURMA_TARIHI) {
            const d = new Date(l.OLUSTURMA_TARIHI);
            if (d.getFullYear() === year) monthlyArr[d.getMonth()]++;
          }
        });
        setChartSeries([{ name: "Eklenen İlanlar", type: "bar", data: monthlyArr }]);

        setStats({
          myListings: myListings.length,
          activeListings: myListings.filter(l => l.AKTIF_DURUM === "aktif").length,
          myOrders: myOrders.length,
          pendingOrders: myOrders.filter(o => o.SIPARIS_DURUMU === "Bekliyor").length,
          myOffers: myOffers.length,
          pendingOffers: myOffers.filter(o => o.DURUM === 0 || o.DURUM === null).length,
          myRevenue,
          isLoading: false,
        });

        setMyRecentOffers(myOffers);
        setRecentPayments(myOdeme);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setStats(prev => ({ ...prev, isLoading: false }));
    } finally {
      setChartLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAdmin]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // ─── CHART OPTIONS ────────────────────────────────────────────
  const chartOptions = useMemo(() => ({
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { width: [2, 0, 0], curve: "smooth" },
    fill: {
      type: ["gradient", "solid", "solid"],
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: MONTHS,
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: isAdmin
      ? [
          { title: { text: "Gelir (₺)" }, labels: { formatter: v => `${Math.round(v).toLocaleString("tr-TR")}₺` } },
          { opposite: true, title: { text: "Adet" }, show: false },
          { show: false },
        ]
      : [{ title: { text: "İlan" } }],
    colors: isAdmin ? ["#556ee6", "#34c38f", "#f1b44c"] : ["#556ee6"],
    legend: { position: "top" },
    tooltip: {
      shared: true,
      intersect: false, // 📌 HATAYI ÇÖZEN SATIR: Burayı false yapmalısın
      y: {
        formatter: (val, { seriesIndex }) => {
          if (isAdmin && seriesIndex === 0) return `${val.toLocaleString("tr-TR")} ₺`;
          return `${val} adet`;
        },
      },
    },
    plotOptions: { bar: { columnWidth: "30%", borderRadius: 3 } },
    grid: { borderColor: "#f1f1f1" },
  }), [isAdmin]);

  // ─── HELPERS ──────────────────────────────────────────────────
  const statusBadge = (s) => {
    const map = {
      "Teslim Edildi": "success", "Bekliyor": "warning", "İptal": "danger",
      "Onaylandı": "info", "Hazırlanıyor": "primary", "Kargoya Verildi": "secondary",
    };
    return <Badge color={map[s] || "light"} className="text-dark">{s || "-"}</Badge>;
  };

  const hesapBadge = (h) => {
    const map = { Admin: "danger", Satıcı: "primary", Alıcı: "success" };
    return <Badge color={map[h] || "secondary"}>{h}</Badge>;
  };

  const teklifBadge = (d) => {
    if (d === 1) return <Badge color="success">Kabul</Badge>;
    if (d === 2) return <Badge color="danger">Red</Badge>;
    return <Badge color="warning">Bekliyor</Badge>;
  };

  // ─── RENDER ───────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Özet Ekranı" />

          {/* ── ADMIN DASHBOARD ───────────────────────────────── */}
          {isAdmin ? (
            <>
              {/* STAT KARTLARI */}
              <Row className="mb-2">
                {[
                  {
                    title: "Toplam Kullanıcı",
                    value: stats.totalUsers,
                    sub: `+${stats.newUsersThisMonth} bu ay`,
                    icon: "bx-group", color: "primary",
                    link: "/contacts-list",
                  },
                  {
                    title: "Aktif İlanlar",
                    value: stats.activeListings,
                    sub: `${stats.totalListings} toplam`,
                    icon: "bx-store-alt", color: "success",
                    link: "/ilan-yonetimi",
                  },
                  {
                    title: "Toplam Sipariş",
                    value: stats.totalOrders,
                    sub: `${stats.pendingOrders} bekliyor`,
                    icon: "bx-cart", color: "warning",
                    link: "/siparis-yonetimi",
                  },
                  {
                    title: "Toplam Gelir",
                    value: `${stats.totalRevenue.toLocaleString("tr-TR")} ₺`,
                    sub: `${stats.monthlyRevenue.toLocaleString("tr-TR")} ₺ bu ay`,
                    icon: "bx-money", color: "info",
                    link: "/crypto-wallet",
                  },
                  {
                    title: "Bekleyen Teklifler",
                    value: stats.pendingOffers,
                    sub: `${stats.acceptedOffers} kabul edildi`,
                    icon: "bx-message-square-dots", color: "danger",
                    link: "/ilan-yonetimi",
                  },
                  {
                    title: "Mağazalar",
                    value: stats.totalStores,
                    sub: `${stats.activeMezats} aktif mezat`,
                    icon: "bx-buildings", color: "secondary",
                    link: "/magaza-yonetimi",
                  },
                ].map((s, i) => (
                  <Col key={i} xs={6} md={4} xl={2}>
                    <Card className="mini-stats-wid mb-3">
                      <CardBody className="py-3 px-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1 overflow-hidden">
                            <p className="text-muted fw-medium mb-1 font-size-12 text-truncate">{s.title}</p>
                            <h5 className="mb-0 font-size-16">
                              {stats.isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                            </h5>
                            <p className="text-muted mb-0 font-size-11 mt-1">{s.sub}</p>
                          </div>
                          <div className={`avatar-sm rounded-circle bg-${s.color} ms-2 flex-shrink-0`}>
                            <span className={`avatar-title rounded-circle bg-${s.color}`}>
                              <i className={`bx ${s.icon} font-size-18`} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* GRAFİK + SİPARİŞ DURUM */}
              <Row>
                <Col xl={8}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <CardTitle className="mb-0">{new Date().getFullYear()} Yılı Özeti</CardTitle>
                          <p className="text-muted font-size-12 mb-0">Aylık gelir, sipariş ve ilan trendi</p>
                        </div>
                      </div>
                      {chartLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <ReactApexChart
                          options={chartOptions}
                          series={chartSeries}
                          type="line"
                          height={300}
                          className="apex-charts"
                        />
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={4}>
                  <Card className="h-100">
                    <CardBody>
                      <CardTitle className="mb-3">Sipariş Durum Dağılımı</CardTitle>
                      {orderStatusDist.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="bx bx-cart display-4 opacity-25 d-block mb-2" />
                          Henüz sipariş yok
                        </div>
                      ) : (
                        <div>
                          {orderStatusDist.map((s, i) => (
                            <div key={i} className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <span className="font-size-13">{s.name}</span>
                                <span className="fw-bold">{s.count}</span>
                              </div>
                              <Progress
                                value={s.percent}
                                color={["primary", "success", "warning", "danger", "info"][i]}
                                className="progress-sm"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* SON SİPARİŞLER + YENİ ÜYELER */}
              <Row>
                <Col xl={7}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Son Siparişler</CardTitle>
                        <Link to="/siparis-yonetimi" className="text-primary font-size-13">
                          Tümünü Gör <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Müşteri</th>
                              <th>Tutar</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentOrders.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-3">Sipariş bulunamadı</td></tr>
                            ) : recentOrders.map(o => (
                              <tr key={o.ID}>
                                <td className="fw-medium text-muted">#{o.ID}</td>
                                <td>{o.KULLANICILAR?.AD_SOYAD || "-"}</td>
                                <td className="fw-bold text-primary">
                                  {o.TOPLAM_TUTAR ? `${Number(o.TOPLAM_TUTAR).toLocaleString("tr-TR")} ₺` : "-"}
                                </td>
                                <td className="text-muted font-size-12">
                                  {o.SIPARIS_TARIHI ? new Date(o.SIPARIS_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>{statusBadge(o.SIPARIS_DURUMU)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={5}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Yeni Üyeler</CardTitle>
                        <Link to="/contacts-list" className="text-primary font-size-13">
                          Tümünü Gör <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Ad Soyad</th>
                              <th>Tip</th>
                              <th>Tarih</th>
                            </tr>
                          </thead>
                          <tbody>
                            {newUsers.length === 0 ? (
                              <tr><td colSpan={3} className="text-center text-muted py-3">Kayıt yok</td></tr>
                            ) : newUsers.map(u => (
                              <tr key={u.ID}>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="avatar-xs rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center"
                                    >
                                      <span className="text-primary fw-bold font-size-11">
                                        {u.AD_SOYAD?.charAt(0) || "?"}
                                      </span>
                                    </div>
                                    <div>
                                      <div className="fw-medium font-size-13">{u.AD_SOYAD}</div>
                                      <div className="text-muted font-size-11">{u.Mail_Adress}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>{hesapBadge(u.HESAP_TURU)}</td>
                                <td className="text-muted font-size-12">
                                  {u.OLUSTURMA_TARIHI ? new Date(u.OLUSTURMA_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* TOP KATEGORİLER + ŞEHİRLER */}
              <Row>
                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">En Çok İlan Açılan Kategoriler</CardTitle>
                        <Link to="/kategori-yonetimi" className="text-primary font-size-13">
                          Yönet <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      {topCategories.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="bx bx-category display-4 opacity-25 d-block mb-2" />Veri yok
                        </div>
                      ) : topCategories.map((c, i) => (
                        <div key={i} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="font-size-13 fw-medium">{c.name}</span>
                            <span className="text-muted font-size-12">{c.count} ilan · %{c.percent}</span>
                          </div>
                          <Progress value={c.percent} color={c.color} className="progress-sm" />
                        </div>
                      ))}
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-3">İlan Yoğunluğuna Göre Şehirler</CardTitle>
                      {topCities.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="bx bx-map display-4 opacity-25 d-block mb-2" />Konum verisi yok
                        </div>
                      ) : (
                        <>
                          <div className="text-center mb-3">
                            <i className="bx bx-map-pin text-primary" style={{ fontSize: 40 }} />
                            <h4 className="mb-0">{topCities[0]?.count} İlan</h4>
                            <p className="text-uppercase text-muted fw-medium">{topCities[0]?.name}</p>
                          </div>
                          <Table size="sm" className="align-middle mb-0">
                            <tbody>
                              {topCities.slice(1).map((c, i) => (
                                <tr key={i}>
                                  <td className="fw-medium text-uppercase" style={{ width: "35%" }}>{c.name}</td>
                                  <td style={{ width: "20%" }}><h6 className="mb-0">{c.count}</h6></td>
                                  <td>
                                    <Progress value={c.percent} color={c.color} className="progress-sm" />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* SON ÖDEMELER */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Son Ödemeler</CardTitle>
                        <Link to="/crypto-wallet" className="text-primary font-size-13">
                          Finansa Git <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Referans No</th>
                              <th>Müşteri</th>
                              <th>Tutar</th>
                              <th>Ödeme Tipi</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentPayments.length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-3">Ödeme kaydı yok</td></tr>
                            ) : recentPayments.map(p => (
                              <tr key={p.ID}>
                                <td className="fw-medium text-muted">
                                  {p.REFERANS_NO || `#${p.ID}`}
                                </td>
                                <td>{p.SIPARIS?.KULLANICILAR?.AD_SOYAD || "-"}</td>
                                <td className="fw-bold text-success">
                                  {Number(p.ODEME_MIKTARI).toLocaleString("tr-TR")} ₺
                                </td>
                                <td><Badge color="light" className="text-dark border">{p.ODEME_TIP || "-"}</Badge></td>
                                <td className="text-muted font-size-12">
                                  {p.ODEME_TARIHI ? new Date(p.ODEME_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>
                                  <Badge color={
                                    p.ODEME_DURUMU?.toLowerCase().includes("tamamland") ? "success"
                                    : p.ODEME_DURUMU?.toLowerCase().includes("iptal") ? "danger"
                                    : "warning"
                                  }>
                                    {p.ODEME_DURUMU || "Bekliyor"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            /* ── NON-ADMIN DASHBOARD ──────────────────────────── */
            <>
              {/* STAT KARTLARI */}
              <Row className="mb-2">
                {[
                  {
                    title: "İlanlarım",
                    value: stats.myListings,
                    sub: `${stats.activeListings} aktif`,
                    icon: "bx-store-alt", color: "primary",
                    link: "/job-list",
                  },
                  {
                    title: "Siparişlerim",
                    value: stats.myOrders,
                    sub: `${stats.pendingOrders} bekliyor`,
                    icon: "bx-cart", color: "success",
                    link: "/job-apply",
                  },
                  {
                    title: "Tekliflerim",
                    value: stats.myOffers,
                    sub: `${stats.pendingOffers} bekliyor`,
                    icon: "bx-message-square-dots", color: "warning",
                    link: "/ecommerce-products",
                  },
                  {
                    title: "Toplam Kazanç",
                    value: `${(stats.myRevenue || 0).toLocaleString("tr-TR")} ₺`,
                    sub: "sipariş toplamı",
                    icon: "bx-money", color: "info",
                    link: "/ecommerce-orders",
                  },
                ].map((s, i) => (
                  <Col key={i} xs={6} md={3}>
                    <Card className="mini-stats-wid mb-3">
                      <CardBody>
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-1 font-size-13">{s.title}</p>
                            <h4 className="mb-0">
                              {stats.isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                            </h4>
                            <p className="text-muted mb-0 font-size-11 mt-1">{s.sub}</p>
                          </div>
                          <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center ms-2`}>
                            <span className={`avatar-title rounded-circle bg-${s.color}`}>
                              <i className={`bx ${s.icon} font-size-20`} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row>
                <Col xl={4}>
                  <WelcomeComp />
                  <MonthlyEarning />
                </Col>
                <Col xl={8}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-3">{new Date().getFullYear()} — Aylık İlan Sayım</CardTitle>
                      {chartLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: 260 }}>
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <ReactApexChart
                          options={chartOptions}
                          series={chartSeries}
                          type="bar"
                          height={260}
                          className="apex-charts"
                        />
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col xl={6}>
                  <ActivityComp />
                </Col>
                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Son Tekliflerim</CardTitle>
                        <Link to="/ecommerce-products" className="text-primary font-size-13">
                          Tümü <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Teklif Tutarı</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myRecentOffers.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-3">
                                  Henüz teklif yok
                                </td>
                              </tr>
                            ) : myRecentOffers.map(t => (
                              <tr key={t.ID}>
                                <td className="text-muted">#{t.ID}</td>
                                <td className="fw-bold text-primary">
                                  {Number(t.TEKLIF_TUTARI || 0).toLocaleString("tr-TR")} ₺
                                </td>
                                <td className="text-muted font-size-12">
                                  {t.TEKLIF_TARIHI ? new Date(t.TEKLIF_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>{teklifBadge(t.DURUM)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-3">Son Ödemelerim</CardTitle>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Referans</th>
                              <th>Tutar</th>
                              <th>Ödeme Tipi</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentPayments.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-3">Ödeme kaydı yok</td></tr>
                            ) : recentPayments.map(p => (
                              <tr key={p.ID}>
                                <td className="text-muted">{p.REFERANS_NO || `#${p.ID}`}</td>
                                <td className="fw-bold text-success">
                                  {Number(p.ODEME_MIKTARI).toLocaleString("tr-TR")} ₺
                                </td>
                                <td><Badge color="light" className="text-dark border">{p.ODEME_TIP || "-"}</Badge></td>
                                <td className="text-muted font-size-12">
                                  {p.ODEME_TARIHI ? new Date(p.ODEME_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>
                                  <Badge color={p.ODEME_DURUMU?.toLowerCase().includes("tamamland") ? "success" : "warning"}>
                                    {p.ODEME_DURUMU || "Bekliyor"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Dashboard);
